import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ThemeMode = 'light' | 'dark' | 'system';
type SidebarView = 'expanded' | 'collapsed' | 'hidden';
type ReadingPanePosition = 'right' | 'bottom' | 'off';
type DensityMode = 'comfortable' | 'cozy' | 'compact';
type AccentColor = 'blue' | 'green' | 'yellow' | 'red' | 'pink' | 'gray' | 'teal' | 'purple' | 'orange';

// Zoho-style theme palettes (light background + accent color)
export interface ThemePalette {
  id: string;
  name: string;
  mode: 'light' | 'dark'; // Actual MUI mode this palette applies
  sidebar: string;  // Sidebar / icon bar color
  content: string;  // Main content area color
  preview: string;  // Preview image indicator
  description: string; // Theme description
  // Legacy aliases kept for backwards compat
  light: string;
  dark: string;
}

export const themePalettes: ThemePalette[] = [
  {
    id: 'dark-navy',
    name: 'Dark Navy',
    mode: 'dark',
    sidebar: '#0d1b2a',
    content: '#1e3a5f',
    light: '#1e3a5f',
    dark: '#0d1b2a',
    preview: 'navy',
    description: 'Professional dark theme with navy blue accents',
  },
  {
    id: 'dark-charcoal',
    name: 'Dark Charcoal',
    mode: 'dark',
    sidebar: '#1a1a1a',
    content: '#2d2d2d',
    light: '#2d2d2d',
    dark: '#1a1a1a',
    preview: 'charcoal',
    description: 'Sleek dark charcoal for distraction-free work',
  },
  {
    id: 'dark-ocean',
    name: 'Dark Ocean',
    mode: 'dark',
    sidebar: '#0f2744',
    content: '#163352',
    light: '#163352',
    dark: '#0f2744',
    preview: 'ocean',
    description: 'Deep ocean blue dark theme',
  },
  {
    id: 'white-gray',
    name: 'Light Gray',
    mode: 'light',
    sidebar: '#1976d2',
    content: '#f8f9fa',
    light: '#f8f9fa',
    dark: '#1976d2',
    preview: 'gray',
    description: 'Clean and minimal light theme',
  },
  {
    id: 'white-clean',
    name: 'Pure White',
    mode: 'light',
    sidebar: '#1a73e8',
    content: '#ffffff',
    light: '#ffffff',
    dark: '#1a73e8',
    preview: 'clean',
    description: 'Crisp white minimal interface',
  },
  {
    id: 'white-warm',
    name: 'Warm Beige',
    mode: 'light',
    sidebar: '#e65100',
    content: '#fafaf7',
    light: '#fafaf7',
    dark: '#e65100',
    preview: 'warm',
    description: 'Warm beige with orange accents',
  },
];

// Accent color palette mapping
export const accentColorPalette: Record<AccentColor, { main: string; light: string; dark: string }> = {
  blue: { main: '#1a73e8', light: '#4285f4', dark: '#1557b0' },
  green: { main: '#34a853', light: '#5bb974', dark: '#1e8e3e' },
  yellow: { main: '#f9ab00', light: '#fdd835', dark: '#f57c00' },
  red: { main: '#ea4335', light: '#f28b82', dark: '#c5221f' },
  pink: { main: '#e91e63', light: '#f48fb1', dark: '#c2185b' },
  gray: { main: '#455a64', light: '#78909c', dark: '#37474f' },
  teal: { main: '#00bcd4', light: '#4dd0e1', dark: '#0097a7' },
  purple: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2' },
  orange: { main: '#ff5722', light: '#ff8a65', dark: '#e64a19' },
};

interface ThemeState {
  mode: ThemeMode;
  actualMode: 'light' | 'dark';
  accentColor: AccentColor;
  selectedPalette: string;
  sidebarView: SidebarView;
  readingPanePosition: ReadingPanePosition;
  densityMode: DensityMode;
  showConversationView: boolean;
  previewOnHover: boolean;
  keyboardShortcutsEnabled: boolean;
  
  // Actions
  setMode: (mode: ThemeMode) => void;
  setAccentColor: (color: AccentColor) => void;
  setSelectedPalette: (paletteId: string) => void;
  setSidebarView: (view: SidebarView) => void;
  setReadingPanePosition: (position: ReadingPanePosition) => void;
  setDensityMode: (density: DensityMode) => void;
  toggleConversationView: () => void;
  togglePreviewOnHover: () => void;
  toggleKeyboardShortcuts: () => void;
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'light',
      actualMode: 'light',
      accentColor: 'blue',
      selectedPalette: 'dark-navy',
      sidebarView: 'expanded',
      readingPanePosition: 'off',
      densityMode: 'comfortable',
      showConversationView: true,
      previewOnHover: true,
      keyboardShortcutsEnabled: true,

      setMode: (mode) => {
        console.log('🎨 themeStore.setMode called with:', mode);
        const actualMode = mode === 'system' ? getSystemTheme() : mode;
        console.log('🎨 themeStore setting actualMode to:', actualMode);
        set({ mode, actualMode });
      },

      setAccentColor: (accentColor) => {
        console.log('🎨 themeStore.setAccentColor called with:', accentColor);
        set({ accentColor });
      },

      setSelectedPalette: (selectedPalette) => {
        console.log('🎨 themeStore.setSelectedPalette called with:', selectedPalette);
        const palette = themePalettes.find(p => p.id === selectedPalette);
        const actualMode = palette ? palette.mode : get().actualMode;
        console.log('🎨 themeStore setting palette → actualMode:', actualMode);
        set({ selectedPalette, mode: actualMode, actualMode });
      },

      setSidebarView: (sidebarView) => set({ sidebarView }),
      
      setReadingPanePosition: (readingPanePosition) => set({ readingPanePosition }),
      
      setDensityMode: (densityMode) => set({ densityMode }),
      
      toggleConversationView: () => set({ showConversationView: !get().showConversationView }),
      
      togglePreviewOnHover: () => set({ previewOnHover: !get().previewOnHover }),
      
      toggleKeyboardShortcuts: () => set({ keyboardShortcutsEnabled: !get().keyboardShortcutsEnabled }),
    }),
    {
      name: 'theme-storage',
      version: 2,
      migrate: (persistedState: any) => {
        return persistedState;
      },
      partialize: (state) => {
        // Include readingPanePosition in persistence for hybrid layout
        return state;
      },
    }
  )
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const state = useThemeStore.getState();
    if (state.mode === 'system') {
      useThemeStore.setState({ actualMode: e.matches ? 'dark' : 'light' });
    }
  });
}
